import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL, formatDate } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
    Upload,
    FileText,
    Image,
    Film,
    Music,
    Archive,
    Trash2,
    Download,
    Loader2,
    HardDrive,
    File,
} from "lucide-react";

const FILE_ICONS = {
    "image/": Image,
    "video/": Film,
    "audio/": Music,
    "application/zip": Archive,
    "application/pdf": FileText,
};

const getFileIcon = (contentType) => {
    for (const [prefix, icon] of Object.entries(FILE_ICONS)) {
        if (contentType?.startsWith(prefix)) return icon;
    }
    return File;
};

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
};

export const DocumentsPage = () => {
    const { token } = useAuth();
    const [files, setFiles] = useState([]);
    const [storage, setStorage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const fileInputRef = useRef(null);

    const fetchData = useCallback(async () => {
        const headers = { Authorization: `Bearer ${token}` };
        const results = await Promise.allSettled([
            fetch(`${API_URL}/files`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
            fetch(`${API_URL}/files/storage-usage`, { headers }).then(r => r.ok ? r.json() : Promise.reject()),
        ]);
        if (results[0].status === "fulfilled") setFiles(results[0].value);
        if (results[1].status === "fulfilled") setStorage(results[1].value);
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await fetch(`${API_URL}/files/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || "Upload failed");
            }
            toast.success("Fichier telecharge avec succes");
            fetchData();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleDelete = async (fileId) => {
        if (!window.confirm("Supprimer ce fichier ?")) return;
        setDeleting(fileId);
        try {
            const res = await fetch(`${API_URL}/files/${fileId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Delete failed");
            toast.success("Fichier supprime");
            fetchData();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setDeleting(null);
        }
    };

    const handleDownload = async (fileId, name) => {
        try {
            const res = await fetch(`${API_URL}/files/${fileId}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Erreur lors du telechargement");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="documents-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Documents</h1>
                    <p className="text-muted-foreground mt-1">Gerez vos fichiers et documents</p>
                </div>
                <div className="flex items-center gap-3">
                    {storage && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="storage-info">
                            <HardDrive className="w-4 h-4" />
                            <span>{formatSize(storage.total_bytes)} utilises</span>
                            <Badge variant="outline" className="text-xs">{storage.file_count} fichiers</Badge>
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleUpload}
                        data-testid="file-input"
                    />
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-btn"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Telecharger
                    </Button>
                </div>
            </div>

            {files.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center">
                        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Aucun document pour le moment</p>
                        <Button
                            variant="outline"
                            className="mt-4"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Telecharger votre premier fichier
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Nom</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Type</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Taille</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Date</th>
                                        <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((f) => {
                                        const Icon = getFileIcon(f.content_type);
                                        return (
                                            <tr key={f.file_id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`file-row-${f.file_id}`}>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                                        <span className="text-sm font-medium truncate max-w-[300px]">{f.original_name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge variant="outline" className="text-xs">{f.content_type?.split("/")[1] || "unknown"}</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {formatSize(f.size_bytes)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                                    {formatDate(f.created_at)}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDownload(f.file_id, f.original_name)}
                                                            data-testid={`download-${f.file_id}`}
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-destructive hover:text-destructive"
                                                            onClick={() => handleDelete(f.file_id)}
                                                            disabled={deleting === f.file_id}
                                                            data-testid={`delete-${f.file_id}`}
                                                        >
                                                            {deleting === f.file_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
